import { useRequest } from 'features/request/useRequest';
import type { DismantlingCoefficientTypeEnum } from 'gql/graphql';
import { useAgGridDataSource } from 'hooks/useAgGridDataSource';
import { uerPriceListUerForDismantlingRatioGql } from 'pages/Project/UerProject/Ratio/CreateOrEditRatio/createOrEditRatioQueries';

import { useEffect, useMemo } from 'react';

const requestId = 'uerPriceListUerForDismantlingRatio';

interface IUseUerPriceListUerForDismantlingRatio {
    filterValues: Record<string, string[]>;
    dataVariables: {
        contractId: number;
        groupType: DismantlingCoefficientTypeEnum | null;
        preselectedUerBaseRowIds: number[] | null[];
    };
}

const getWhereVariables = (filterValues?: Record<string, string[]>) => {
    if (!filterValues) {
        return undefined;
    }

    return {
        and: Object.entries(filterValues).reduce((acc: any, [key, value]) => {
            if (!value.length) {
                return acc;
            }

            if (key === 'groupName') {
                return [
                    ...acc,
                    {
                        uerGroupName: { in: value },
                    },
                ];
            }

            if (value.length) {
                acc = [
                    ...acc,
                    {
                        [key]: { in: value },
                    },
                ];
            }

            return acc;
        }, []),
    };
};

export const useUerPriceListUerForDismantlingRatio = (props: IUseUerPriceListUerForDismantlingRatio) => {
    const { filterValues, dataVariables } = props;

    const [uerPriceListUerForDismantlingRatioData, , , uerPriceListUerForDismantlingRatioRequest] = useRequest({
        requestId,
        query: uerPriceListUerForDismantlingRatioGql,
        withEffect: false,
    });

    const variables = useMemo(
        () => ({
            ...dataVariables,
            where: getWhereVariables(filterValues),
        }),
        [dataVariables, filterValues],
    );

    const { dataSource, getRowParams } = useAgGridDataSource({
        requestId,
        requestFn: uerPriceListUerForDismantlingRatioRequest,
        requestVariables: variables,
    });

    useEffect(() => {
        if (
            uerPriceListUerForDismantlingRatioData?.uerPriceListUerForDismantlingRatio?.items &&
            getRowParams?.current
        ) {
            getRowParams?.current?.successCallback(
                uerPriceListUerForDismantlingRatioData?.uerPriceListUerForDismantlingRatio?.items,
                uerPriceListUerForDismantlingRatioData?.uerPriceListUerForDismantlingRatio?.totalCount,
            );
        }
    }, [
        getRowParams,
        uerPriceListUerForDismantlingRatioData?.uerPriceListUerForDismantlingRatio?.items,
        uerPriceListUerForDismantlingRatioData?.uerPriceListUerForDismantlingRatio?.totalCount,
    ]);

    return dataSource;
};
